import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useAtomValue } from "jotai";
import { instanceSliceInfoMapAtom } from "@/atoms/selectionAtoms";
import { slotAssignmentsSelector } from "@/atoms/hangingProtocolAtoms";
import type { WindowLevel } from "@/components/CaseViewer";
import { Hint } from "@/components/devicePanel/Hint";
import { SharedTextField } from "@/components/jointReview/SharedTextField";
import { DEFAULT_WINDOWING } from "@/constants/windowPresets";
import { verdictErrorText } from "@/hooks/useDevicePanel";
import {
  useSaveCall,
  useSaveNote,
  useSaveSessionNotes,
  useSessionNotes,
} from "@/hooks/useJointReview";
import type { CallBody } from "@/hooks/useJointReview";
import { MARKER_COLOR, markColor, useJointReviewMasks } from "@/hooks/useJointReviewMasks";
import {
  CALLS,
  NOTE_MAX_LENGTH,
  RATINGS,
  SESSION_NOTES_MAX_LENGTH,
  callLabel,
  callText,
  itemName,
  itemState,
  type JointCall,
  type JointReviewStudy,
  type Mark,
  type Reader,
  type StudyItem,
  type Voices,
} from "@/types/jointReview";
import { parseReaderConfidence } from "@/utils/readerConfidence";

/** The item selected last (item ids repeat across studies, so with its study),
 * and a count that changes on every selection, so selecting the same item again
 * jumps again. */
export interface Selection {
  studyUid: string | null;
  itemId: string | null;
  nonce: number;
}

const rgb = (reader: Reader | null) => `rgb(${markColor(reader).slice(0, 3).join(" ")})`;

function Legend({ blind }: { blind: boolean }) {
  if (blind)
    return (
      <div data-testid="joint-review-legend" className="text-[10.5px] text-fg-muted">
        <span className="flex items-center gap-1">
          <span
            aria-hidden
            className="inline-block h-2.5 w-2.5 rounded-full border-2"
            style={{
              borderColor: `rgb(${MARKER_COLOR.slice(0, 3).join(" ")})`,
            }}
          />
          Item marker: call what is inside the circle
        </span>
      </div>
    );
  return (
    <div
      data-testid="joint-review-legend"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] text-fg-muted"
    >
      {(["A", "B"] as const).map((reader) => (
        <span key={reader} className="flex items-center gap-1">
          <span
            aria-hidden
            className="inline-block h-0 w-4 border-t-2"
            style={{ borderColor: rgb(reader) }}
          />
          Reader {reader}
        </span>
      ))}
      <span className="flex items-center gap-1">
        <span aria-hidden className="inline-block h-0 w-4 border-t-2 border-dashed border-fg-dim" />
        Marked by both (context)
      </span>
    </div>
  );
}

const STATE_TEXT = {
  final: (item: StudyItem) => `Final: ${callLabel(item.consensus!)}`,
  agreed_needs_note: (item: StudyItem) =>
    `Agreed: ${callLabel(item.consensus!)}. Add a note to finish`,
  not_agreed: () => "Not agreed yet",
  waiting: () => "Waiting for both calls",
};

/**
 * One item: the mark's name and size (a button that selects it and jumps to its
 * key slice), this reader's call, the other reader's call, where the two stand,
 * and the shared note that is the item's reason. Final (both calls match and
 * the note is filled) shows as a solid signal edge with a ✓ and the word Final;
 * agreed without a note, dashed. While the card has focus (outside the note),
 * 1 / 2 / 3 make this reader's call and j goes to the next item that is not final.
 * An observer sees both calls and the note, and changes neither.
 */
function ItemCard({
  item,
  items,
  mark,
  current,
  onSelect,
  onNext,
  saveCall,
  saveNote,
  voices,
}: {
  item: StudyItem;
  items: StudyItem[];
  mark: Mark | undefined;
  current: boolean;
  onSelect: () => void;
  onNext: () => void;
  saveCall: (call: JointCall) => Promise<unknown>;
  saveNote: (text: string) => Promise<unknown>;
  voices: Voices;
}) {
  const [error, setError] = useState<string | null>(null);
  const state = itemState(item);
  const name = itemName(items, item);
  // The mark's size and the reader's own confidence in it, when the save has them.
  const confidence = parseReaderConfidence(mark?.reader_confidence);
  const facts = [
    mark?.long_axis_mm ? `${mark.long_axis_mm.toFixed(1)} mm` : null,
    confidence !== null ? `conf ${confidence}` : null,
  ].filter((fact): fact is string => fact !== null);
  const call = async (value: JointCall) => {
    setError(null);
    try {
      await saveCall(value);
    } catch (failure) {
      setError(verdictErrorText(failure));
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
      return;
    const chosen = voices.observer ? undefined : CALLS.find(({ key }) => key === event.key);
    const act = chosen ? () => void call(chosen.value) : event.key === "j" ? onNext : null;
    if (!act) return;
    event.preventDefault();
    event.stopPropagation();
    act();
  };
  const edge =
    state === "final"
      ? "shadow-[inset_3px_0_0_var(--color-signal)]"
      : state === "agreed_needs_note"
        ? "border-l-2 border-dashed border-l-border-strong"
        : "";
  return (
    <article
      aria-label={name}
      data-item={item.item_id}
      data-current={current || undefined}
      data-state={state}
      onKeyDown={onKeyDown}
      className={`flex flex-col gap-1.5 border-b border-border px-3 py-2 ${edge} ${
        current ? "bg-surface-2" : ""
      }`}
    >
      <div className="flex items-baseline gap-2">
        <button
          type="button"
          data-select
          onClick={onSelect}
          className="flex items-baseline gap-1.5 text-left font-mono text-[12px] text-fg hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
        >
          <span
            aria-hidden
            className="inline-block h-0 w-3 translate-y-[-3px] border-t-2"
            style={{ borderColor: rgb(item.reader) }}
          />
          {name}
        </button>
        {facts.length > 0 && (
          <span data-testid="mark-facts" className="font-mono text-[10.5px] text-fg-dim">
            {facts.join(" · ")}
          </span>
        )}
        <span
          data-testid="item-state"
          className={`ml-auto text-[10.5px] ${state === "final" ? "text-fg" : "text-fg-muted"}`}
        >
          {state === "final" && <span aria-hidden>✓ </span>}
          {STATE_TEXT[state](item)}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-fg-dim">
        <span>{voices.myCall}:</span>
        <span role="group" aria-label={`${voices.myCall}, ${name}`} className="flex">
          {CALLS.map(({ value, label, key }) => (
            <button
              key={value}
              type="button"
              data-call={value}
              aria-pressed={item.my_call === value}
              aria-keyshortcuts={voices.observer ? undefined : key}
              disabled={voices.observer}
              onClick={() => void call(value)}
              className="-ml-px border border-border-strong px-1.5 py-px text-fg-muted first:ml-0 first:rounded-l-xs last:rounded-r-xs enabled:hover:text-fg disabled:cursor-default focus-visible:relative focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal aria-pressed:relative aria-pressed:border-signal aria-pressed:bg-signal aria-pressed:text-fg"
            >
              {label}
            </button>
          ))}
        </span>
        {error && (
          <Hint label={error}>
            <span tabIndex={0} className="text-warn underline decoration-dotted underline-offset-2">
              Not saved<span className="sr-only">: {error}</span>
            </span>
          </Hint>
        )}
      </div>
      {item.independent && <IndependentCalls item={item} voices={voices} />}
      {mark && !mark.in_mask && (
        <p className="text-[10.5px] text-fg-dim">
          No outline: a later overlapping mark by the same reader replaced it. Its slice is shown.
        </p>
      )}
      <p data-testid="other-call" className="text-[10.5px] text-fg-muted">
        {voices.otherCall}: {item.other_call ? callLabel(item.other_call) : "no call yet"}
      </p>
      <SharedTextField
        value={item.note}
        onSave={saveNote}
        label={`Note, ${name}`}
        placeholder={voices.observer ? "No reason yet" : "Reason (shared)"}
        maxLength={NOTE_MAX_LENGTH}
        editors={voices.editor}
        readOnly={voices.observer}
      />
    </article>
  );
}

/** Both readers' independent calls on an item, for the joint session. */
function IndependentCalls({ item, voices }: { item: StudyItem; voices: Voices }) {
  const { mine, other } = item.independent!;
  return (
    <p data-testid="independent-calls" className="text-[10.5px] text-fg-muted">
      Independent: {voices.editor.you} {mine ? callText(mine) : "no call"};{" "}
      {voices.editor.other_reader} {other ? callText(other) : "no call"}
    </p>
  );
}

const keyedCall = (key: string) => CALLS.find((entry) => entry.key === key)?.value ?? null;

/**
 * One item of a blind review's independent phase: its marker (a button that
 * selects it and jumps to its slice), and this reader's own call, which nobody
 * else sees yet. Present and absent save with a rating on the anchors;
 * indeterminate (the image can't be read) saves with a reason. 1 / 2 / 3 pick
 * the call while the card has focus, and j goes to the next uncalled item. An
 * observer sees the first reader's call and changes nothing.
 */
function BlindCallCard({
  item,
  current,
  onSelect,
  onNext,
  saveCall,
  voices,
}: {
  item: StudyItem;
  current: boolean;
  onSelect: () => void;
  onNext: () => void;
  saveCall: (body: Omit<CallBody, "item_id">) => Promise<unknown>;
  voices: Voices;
}) {
  const mine = item.independent?.mine ?? null;
  const [choice, setChoice] = useState<JointCall | null>(mine?.call ?? null);
  const [reason, setReason] = useState(mine?.reason ?? "");
  const [error, setError] = useState<string | null>(null);
  const name = `Item ${item.item_id}`;
  const save = async (body: Omit<CallBody, "item_id">) => {
    setError(null);
    try {
      await saveCall(body);
    } catch (failure) {
      setError(verdictErrorText(failure));
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)
      return;
    const chosen = voices.observer ? null : keyedCall(event.key);
    if (!chosen && event.key !== "j") return;
    event.preventDefault();
    event.stopPropagation();
    if (chosen) setChoice(chosen);
    else onNext();
  };
  const pending = choice !== null && (mine?.call !== choice || choice === "indeterminate");
  return (
    <article
      aria-label={name}
      data-item={item.item_id}
      data-current={current || undefined}
      data-state={mine ? "called" : "open"}
      onKeyDown={onKeyDown}
      className={`flex flex-col gap-1.5 border-b border-border px-3 py-2 ${
        mine ? "shadow-[inset_3px_0_0_var(--color-signal)]" : ""
      } ${current ? "bg-surface-2" : ""}`}
    >
      <div className="flex items-baseline gap-2">
        <button
          type="button"
          data-select
          onClick={onSelect}
          className="font-mono text-[12px] text-fg hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
        >
          {name}
        </button>
        <span data-testid="item-state" className="ml-auto text-[10.5px] text-fg-muted">
          {mine ? (
            <>
              <span aria-hidden>✓ </span>
              {callText(mine)}
            </>
          ) : (
            "Not called yet"
          )}
        </span>
      </div>
      <span role="group" aria-label={`${voices.myCall}, ${name}`} className="flex text-[10.5px]">
        {CALLS.map(({ value, label, key }) => (
          <button
            key={value}
            type="button"
            data-call={value}
            aria-pressed={choice === value}
            aria-keyshortcuts={voices.observer ? undefined : key}
            disabled={voices.observer}
            onClick={() => setChoice(value)}
            className="-ml-px border border-border-strong px-1.5 py-px text-fg-muted first:ml-0 first:rounded-l-xs last:rounded-r-xs hover:text-fg focus-visible:relative focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal aria-pressed:relative aria-pressed:border-signal aria-pressed:bg-signal aria-pressed:text-fg"
          >
            {label}
          </button>
        ))}
      </span>
      {choice && choice !== "indeterminate" && (
        <span role="group" aria-label={`Rating, ${name}`} className="flex flex-wrap text-[10.5px]">
          {RATINGS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              data-rating={value}
              aria-pressed={mine?.call === choice && mine.rating === value}
              disabled={voices.observer}
              onClick={() => void save({ call: choice, rating: value })}
              className="-ml-px border border-border-strong px-1.5 py-px text-fg-muted first:ml-0 first:rounded-l-xs last:rounded-r-xs hover:text-fg focus-visible:relative focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal aria-pressed:relative aria-pressed:border-signal aria-pressed:bg-signal aria-pressed:text-fg"
            >
              {label} {value}
            </button>
          ))}
        </span>
      )}
      {choice === "indeterminate" && !voices.observer && (
        <form
          className="flex items-start gap-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            void save({ call: "indeterminate", reason });
          }}
        >
          <textarea
            aria-label={`Why it can't be read, ${name}`}
            value={reason}
            maxLength={NOTE_MAX_LENGTH}
            rows={2}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why the image can't be read (artifact, coverage)"
            className="min-w-0 flex-1 resize-y rounded-xs border border-border-strong bg-bg px-1.5 py-1 text-[11px] text-fg"
          />
          <button
            type="submit"
            disabled={!reason.trim()}
            className="rounded-xs border border-border-strong px-2 py-0.5 text-[11px] text-fg-muted hover:text-fg disabled:opacity-60"
          >
            Save
          </button>
        </form>
      )}
      {pending && choice !== "indeterminate" && !voices.observer && (
        <p className="text-[10.5px] text-fg-dim">Pick a rating to save the call.</p>
      )}
      {error && (
        <Hint label={error}>
          <span
            tabIndex={0}
            className="text-[10.5px] text-warn underline decoration-dotted underline-offset-2"
          >
            Not saved<span className="sr-only">: {error}</span>
          </span>
        </Hint>
      )}
    </article>
  );
}

function SessionNotes({ projectId, voices }: { projectId: string; voices: Voices }) {
  const [open, setOpen] = useState(false);
  const notes = useSessionNotes(projectId, open);
  const save = useSaveSessionNotes(projectId);
  return (
    <section aria-label="Session notes" className="border-t border-border px-3 py-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="text-[11px] text-fg-muted hover:text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
      >
        {open ? "▾" : "▸"} Session notes
      </button>
      {open &&
        (notes.data ? (
          <div className="pt-1.5">
            <SharedTextField
              value={notes.data}
              onSave={save.mutateAsync}
              label="Session notes"
              placeholder="Shared notes for this session"
              maxLength={SESSION_NOTES_MAX_LENGTH}
              rows={5}
              editors={voices.editor}
              readOnly={voices.observer}
            />
          </div>
        ) : (
          <p className="pt-1.5 text-[11px] text-fg-dim">
            {notes.isError ? "Session notes could not load." : "Loading…"}
          </p>
        ))}
    </section>
  );
}

/**
 * The joint review of one study, beside its images: a legend, progress, the
 * follow switch, a button that hides every outline to show the raw image, one
 * card per item, Next, and the session notes. Selecting an
 * item jumps the viewer to its mark's key slice once that slice is known. In a
 * blind review's independent phase the cards take this reader's own calls and
 * there is no Follow or session notes, since nothing is shared. An observer
 * gets the same rail, read-only.
 */
export function JointReviewRail({
  projectId,
  studyUid,
  study,
  selection,
  progress,
  allDone,
  shared,
  follow,
  onFollowChange,
  onSelect,
  onNext,
  jumpToSlice,
  voices,
}: {
  projectId: string;
  studyUid: string;
  study: JointReviewStudy;
  selection: Selection;
  progress: string;
  allDone: string | null;
  shared: boolean;
  follow: boolean;
  onFollowChange: (follow: boolean) => void;
  onSelect: (itemId: string) => void;
  onNext: () => void;
  jumpToSlice: (sopInstanceUid: string, window: WindowLevel) => void;
  voices: Voices;
}) {
  // A selection made in another study names nothing here (item ids repeat).
  const selected = selection.studyUid === studyUid ? selection.itemId : null;
  // This reader's view only (not shared through Follow); each study opens with
  // the marks shown, since the rail mounts per study.
  const [marksVisible, setMarksVisible] = useState(true);
  useJointReviewMasks(study, selected, marksVisible);
  const saveCall = useSaveCall(projectId, studyUid);
  const saveNote = useSaveNote(projectId, studyUid);
  const sliceInfo = useAtomValue(instanceSliceInfoMapAtom);
  const viewerReady = Object.values(useAtomValue(slotAssignmentsSelector)).some(Boolean);
  const markOf = (itemId: string | null) => study.marks.find((mark) => mark.item_id === itemId);

  // Jump once per selection, as soon as the viewer knows the mark's key slice.
  const jumped = useRef<number | null>(null);
  const target = markOf(selected)?.key_slice_sop_instance_uid;
  const targetLoaded =
    !!target && Object.values(sliceInfo).some((instances) => target in instances);
  useEffect(() => {
    if (!target || !targetLoaded || !viewerReady || jumped.current === selection.nonce) return;
    jumped.current = selection.nonce;
    jumpToSlice(target, DEFAULT_WINDOWING);
    document
      .querySelector(`[data-item="${CSS.escape(selected!)}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [target, targetLoaded, viewerReady, selection, selected, jumpToSlice]);

  return (
    <div data-joint-review-rail className="flex h-full flex-col bg-bg-2 text-fg">
      <header className="flex flex-col gap-1.5 border-b border-border px-3 pb-2 pt-2.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="text-[13px] font-semibold">{shared ? "Joint review" : "Your calls"}</h2>
          <span data-testid="progress" className="font-mono text-[11px] tabular-nums text-fg-muted">
            {progress}
          </span>
          {voices.observer && (
            <span data-testid="observing" className="text-[11px] text-fg-muted">
              Observing, read-only
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {shared && (
              <label className="flex items-center gap-1.5 text-[11px] text-fg-muted">
                <input
                  type="checkbox"
                  checked={follow}
                  onChange={(event) => onFollowChange(event.target.checked)}
                  className="accent-[var(--color-signal)]"
                />
                {voices.observer ? "Follow the readers" : "Follow the other reader"}
              </label>
            )}
            <button
              type="button"
              aria-pressed={!marksVisible}
              onClick={() => setMarksVisible((shown) => !shown)}
              className="rounded-xs border border-border-strong px-2 py-0.5 text-[11px] text-fg-muted hover:text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
            >
              {marksVisible ? "Hide marks" : "Show marks"}
            </button>
          </div>
        </div>
        <Legend blind={study.marks.some((mark) => mark.reader === null)} />
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {study.items.map((item) =>
          item.phase === "independent" ? (
            <BlindCallCard
              key={item.item_id}
              item={item}
              current={item.item_id === selected}
              onSelect={() => onSelect(item.item_id)}
              onNext={onNext}
              saveCall={(body) => saveCall.mutateAsync({ item_id: item.item_id, ...body })}
              voices={voices}
            />
          ) : (
            <ItemCard
              key={item.item_id}
              item={item}
              items={study.items}
              mark={markOf(item.item_id)}
              current={item.item_id === selected}
              onSelect={() => onSelect(item.item_id)}
              onNext={onNext}
              saveCall={(call) => saveCall.mutateAsync({ item_id: item.item_id, call })}
              saveNote={(text) => saveNote.mutateAsync({ item_id: item.item_id, text })}
              voices={voices}
            />
          ),
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-border px-3 py-2">
        {allDone ? (
          <p role="status" className="text-[11.5px] text-fg">
            ✓ {allDone}
          </p>
        ) : (
          <button
            type="button"
            aria-keyshortcuts="j"
            onClick={onNext}
            className="rounded-xs border border-border-strong px-2 py-0.5 text-[11.5px] text-fg-muted hover:text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal"
          >
            Next item ›
          </button>
        )}
      </div>
      {shared && <SessionNotes projectId={projectId} voices={voices} />}
    </div>
  );
}
